import type { PartnerReferralCouponFactRecord } from '@navio/nsdk';
import { ReferralJourneyStatus } from '@navio/nsdk';

export type ReferralStaffStatus = 'waiting' | 'successful' | 'expired';

export const toReferralStaffStatus = (status: ReferralJourneyStatus): ReferralStaffStatus => {
  if (status === ReferralJourneyStatus.EXPIRED) {
    return 'expired';
  }
  if (status === ReferralJourneyStatus.EARNED || status === ReferralJourneyStatus.REDEEMED) {
    return 'successful';
  }
  return 'waiting';
};

export const REFERRAL_STAFF_STATUS_LABEL: Record<ReferralStaffStatus, string> = {
  waiting: 'Waiting',
  successful: 'Successful',
  expired: 'Expired'
};

export const REFERRAL_STAFF_STATUS_DESCRIPTION: Record<ReferralStaffStatus, string> = {
  waiting: 'Got the invite, still has time to book.',
  successful: 'Booked in time, so the referrer earned a reward.',
  expired: 'Invite ran out with no reward.'
};

export const REFERRAL_COUNT_LABEL = {
  successful: 'Successful',
  waiting: 'Waiting',
  expired: 'Expired'
} as const;

export const latestInviteExpiresAt = (
  inviteCoupons: PartnerReferralCouponFactRecord[]
): PartnerReferralCouponFactRecord['expiresAt'] => {
  const dates = inviteCoupons.map((coupon) => coupon.expiresAt).filter((date) => date != null);
  if (dates.length === 0) {
    return null;
  }
  return dates.reduce((latest, date) => (date > latest ? date : latest));
};

export const successfulReferralCount = (earnedCount: number, redeemedCount: number): number =>
  earnedCount + redeemedCount;

export const shouldShowConnectedReferral = ({
  brandId,
  ownBrandId,
  isEnabled,
  referredCount
}: {
  brandId: number;
  ownBrandId: number;
  isEnabled: boolean;
  referredCount: number;
}): boolean => {
  const hasJourneys: boolean = referredCount > 0;
  if (brandId === ownBrandId) {
    return isEnabled || hasJourneys;
  }
  return hasJourneys;
};

export type ReferralStaffStatusTab = 'all' | ReferralStaffStatus;

export const referralJourneyStatusSubtext = (status: ReferralJourneyStatus): string => {
  if (status === ReferralJourneyStatus.REDEEMED) {
    return 'Used their reward';
  }
  if (status === ReferralJourneyStatus.EARNED) {
    return 'Completed their booking';
  }
  if (status === ReferralJourneyStatus.EXPIRED) {
    return 'Invite expired';
  }
  return 'Waiting for order';
};

export const filterByReferralStaffTab = <T extends { status: ReferralJourneyStatus }>(
  items: T[],
  tab: ReferralStaffStatusTab
): T[] => {
  if (tab === 'all') {
    return items;
  }
  return items.filter((item) => toReferralStaffStatus(item.status) === tab);
};

export const countReferralsByStaffTab = (
  items: { status: ReferralJourneyStatus }[]
): Record<ReferralStaffStatusTab, number> => ({
  all: items.length,
  waiting: items.filter((item) => toReferralStaffStatus(item.status) === 'waiting').length,
  successful: items.filter((item) => toReferralStaffStatus(item.status) === 'successful').length,
  expired: items.filter((item) => toReferralStaffStatus(item.status) === 'expired').length
});
